/**
 * useTour — page-scoped Shepherd.js tour hook.
 *
 * Each page that opts in calls `useTour({ pageKey, steps })`. The hook:
 *   1. Asks the server (via useAuth's cached user) whether this user is
 *      still within their first-three-sign-ins tour window.
 *   2. Tracks per-session sessionStorage flags so a tour for a given
 *      page fires at most once per session (a page re-mount from
 *      navigation doesn't re-trigger).
 *   3. Waits for the page's target selectors to actually exist in the
 *      DOM before starting — data-fetch flows can delay element
 *      rendering, and Shepherd silently skips steps whose attachTo
 *      element isn't present.
 *
 * The tour is intentionally lightweight per page (2–4 steps max) per
 * product spec.
 */
import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useAuth } from '../context/AuthContext.tsx';

export interface TourStep {
  /** Stable id used by Shepherd. Keep unique within a tour. */
  id: string;
  /** Uppercase label shown in the tooltip header (e.g. "STEP 1 · OVERVIEW"). */
  title: string;
  /** Body copy — plain text or HTML. */
  text: string;
  /** CSS selector of the element to attach the step to. */
  attachTo: string;
  /** Placement of the tooltip relative to attachTo. */
  on?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
}

interface UseTourOptions {
  /** Short identifier for this page's tour — used in sessionStorage. */
  pageKey: string;
  steps: TourStep[];
  /**
   * Additional gate — e.g., wait until data is loaded. When false the
   * tour won't fire this render.
   */
  enabled?: boolean;
}

const MAX_TOUR_SIGNINS = 3;

export function useTour({ pageKey, steps, enabled = true }: UseTourOptions) {
  const { user } = useAuth();

  // Always read the latest steps via a ref so the effect doesn't
  // re-trigger (and cancel the running tour) on every parent render.
  // Consumers pass a fresh array literal each render, which used to
  // blow up the dep array and cause the cleanup to cancel the tour
  // immediately after it started.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  // Stable scalars drive re-fires. We intentionally do NOT include
  // `steps` in the dep array — steps are conceptually static per
  // component mount, and array-ref churn would otherwise tear down
  // the tour on every render.
  const signinCount = user?.tourSigninsSeen;
  const userId = user?.id;

  useEffect(() => {
    // Verbose diagnostic logging while we debug tour-not-firing issues.
    // Safe to leave on; runs once per tour eligibility check (~3x total).
    const log = (msg: string, extra?: unknown) =>
      // eslint-disable-next-line no-console
      console.log(`[tour:${pageKey}] ${msg}`, extra ?? '');

    if (!enabled) {
      log('skipped: enabled=false');
      return;
    }
    if (!userId || signinCount === undefined) {
      log('skipped: user not loaded yet', { userId, signinCount });
      return;
    }
    if (signinCount >= MAX_TOUR_SIGNINS) {
      log(`skipped: already saw tour ${signinCount} times`);
      return;
    }

    // Don't re-fire the same page's tour twice in one session. Separate
    // key per page AND per signinCount so admin-triggered "Reset tour"
    // (which pushes the count back to 0) naturally invalidates stale
    // flags from earlier sign-ins without needing the user to clear
    // sessionStorage manually.
    const sessionKey = `ojb.tour.${pageKey}.${signinCount}.shown`;
    if (sessionStorage.getItem(sessionKey)) {
      log('skipped: sessionStorage flag already set for this signin count');
      return;
    }

    // Best-effort cleanup of legacy / prior-signin flags for this page
    // so sessionStorage doesn't accumulate dead keys over the tour
    // window. Safe to run every effect — iterates at most ~3 keys.
    for (let i = 0; i <= MAX_TOUR_SIGNINS; i += 1) {
      if (i !== signinCount) sessionStorage.removeItem(`ojb.tour.${pageKey}.${i}.shown`);
    }
    // Also wipe the old un-count-suffixed key from earlier deploys.
    sessionStorage.removeItem(`ojb.tour.${pageKey}.shown`);

    const currentSteps = stepsRef.current;
    const firstSelector = currentSteps[0]?.attachTo;
    if (!firstSelector) {
      log('skipped: no steps or first step has no attachTo');
      return;
    }

    log('eligible; waiting for target', { firstSelector, signinCount });

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // ~5s at 100ms
    // Track the tour instance WE started so cleanup only cancels our
    // own. Using Shepherd.activeTour globally would clobber a tour
    // that another component may have started (or one we intentionally
    // leave running across route changes).
    let ownTour: InstanceType<typeof Shepherd.Tour> | null = null;

    function tryStart() {
      if (cancelled) return;
      const el = document.querySelector(firstSelector);
      if (!el) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          log(`giving up: target ${firstSelector} never appeared`);
          return;
        }
        setTimeout(tryStart, 100);
        return;
      }
      log(`target found after ${attempts} attempts; starting tour`);
      start();
    }

    function start() {
      if (cancelled) {
        log('start() aborted: cancelled flag set');
        return;
      }

      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          scrollTo: { behavior: 'smooth', block: 'center' },
          cancelIcon: { enabled: true },
          classes: 'ojb-tour-step',
          modalOverlayOpeningPadding: 6,
          modalOverlayOpeningRadius: 6,
        },
      });

      currentSteps.forEach((step, idx) => {
        const isLast = idx === currentSteps.length - 1;
        tour.addStep({
          id: step.id,
          title: step.title,
          text: step.text,
          attachTo: { element: step.attachTo, on: step.on ?? 'bottom' },
          buttons: isLast
            ? [{ text: 'Done', action: tour.complete.bind(tour) }]
            : [
                { text: 'Skip', secondary: true, action: tour.cancel.bind(tour) },
                { text: 'Next', action: tour.next.bind(tour) },
              ],
        });
      });

      ownTour = tour;
      // Set the "shown this session" flag AFTER the tour actually
      // starts — otherwise a cancelled start (e.g., target never
      // appeared, effect torn down) would leave the flag set and
      // permanently block future attempts.
      try {
        tour.start();
        sessionStorage.setItem(sessionKey, '1');
        log('tour.start() called successfully');
      } catch (err) {
        log('tour.start() threw', err);
      }
    }

    tryStart();

    return () => {
      cancelled = true;
      // Only cancel the tour WE started. Don't touch
      // Shepherd.activeTour globally — that could kill a peer
      // component's tour.
      if (ownTour && Shepherd.activeTour === ownTour) {
        ownTour.cancel();
      }
    };
    // steps deliberately excluded — read fresh via stepsRef so a new
    // array literal on every render doesn't tear down a running tour.
  }, [enabled, userId, signinCount, pageKey]);
}
